import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';

const BUCKET = 'pups';

const toPublicUrl = (path) => {
  if (!supabase || !path) return null;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  return data?.publicUrl || null;
};

const normalizeRow = (row) => {
  const imagePaths = Array.isArray(row.image_paths) ? row.image_paths : [];
  const images = imagePaths.map((p) => toPublicUrl(p)).filter(Boolean);
  return {
    id: row.id,
    title: row.name || 'Pup',
    sex: row.sex || '',
    breed: row.breed || '',
    status: row.status || 'Available',
    price: typeof row.price === 'number' ? row.price : row.price ? Number(row.price) : null,
    age: row.age || 'Ask for details',
    weight: row.weight || 'Ask for details',
    size: row.size || 'Ask for details',
    description: row.description || 'Message us for more details.',
    images: images.length ? images : [],
    image_paths: imagePaths
  };
};

export const pupsStoreEnabled = () => isSupabaseConfigured && Boolean(supabase);

export async function fetchPups() {
  if (!pupsStoreEnabled()) return [];
  const { data, error } = await supabase
    .from('pups')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []).map(normalizeRow).map((p) => ({ ...p, images: p.images.length ? p.images : [] }));
}

export async function createPup(input) {
  if (!pupsStoreEnabled()) throw new Error('Supabase not configured');
  const id = input.id || crypto.randomUUID();
  const row = {
    id,
    name: input.title || 'Pup',
    sex: input.sex || '',
    breed: input.breed || '',
    status: input.status || 'Available',
    price: typeof input.price === 'number' ? input.price : input.price ? Number(input.price) : null,
    age: input.age || 'Ask for details',
    weight: input.weight || 'Ask for details',
    size: input.size || 'Ask for details',
    description: input.description || '',
    image_paths: Array.isArray(input.image_paths) ? input.image_paths : []
  };

  const { data, error } = await supabase.from('pups').insert(row).select('*').single();
  if (error) throw error;
  return normalizeRow(data);
}

export async function updatePup(id, patch) {
  if (!pupsStoreEnabled()) throw new Error('Supabase not configured');
  const rowPatch = {};
  if (patch.title !== undefined) rowPatch.name = patch.title;
  if (patch.sex !== undefined) rowPatch.sex = patch.sex;
  if (patch.breed !== undefined) rowPatch.breed = patch.breed;
  if (patch.status !== undefined) rowPatch.status = patch.status;
  if (patch.price !== undefined) rowPatch.price = patch.price === '' || patch.price === null ? null : Number(patch.price);
  if (patch.age !== undefined) rowPatch.age = patch.age;
  if (patch.weight !== undefined) rowPatch.weight = patch.weight;
  if (patch.size !== undefined) rowPatch.size = patch.size;
  if (patch.description !== undefined) rowPatch.description = patch.description;
  if (patch.image_paths !== undefined) rowPatch.image_paths = patch.image_paths;

  const { data, error } = await supabase.from('pups').update(rowPatch).eq('id', id).select('*').single();
  if (error) throw error;
  return normalizeRow(data);
}

export async function deletePup(id, imagePaths = []) {
  if (!pupsStoreEnabled()) throw new Error('Supabase not configured');
  await supabase.from('pups').delete().eq('id', id);
  if (Array.isArray(imagePaths) && imagePaths.length) {
    await supabase.storage.from(BUCKET).remove(imagePaths);
  }
}

export async function removePupImages(imagePaths = []) {
  if (!pupsStoreEnabled()) throw new Error('Supabase not configured');
  if (!Array.isArray(imagePaths) || !imagePaths.length) return;
  await supabase.storage.from(BUCKET).remove(imagePaths);
}

export async function uploadPupImages(pupId, files) {
  if (!pupsStoreEnabled()) throw new Error('Supabase not configured');
  const list = Array.from(files || []);
  if (!list.length) return [];

  const uploadedPaths = [];

  for (const file of list) {
    const safeName = String(file.name || 'image').replaceAll(' ', '-');
    const path = `pups/${pupId}/${Date.now()}-${safeName}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
    if (error) throw error;
    uploadedPaths.push(path);
  }

  return uploadedPaths;
}
